import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bell,
  Plus,
  Search,
  Trash2,
  Send,
  CheckCircle,
  Clock,
  Users,
  User,
  X,
  Loader,
  Filter,
  RefreshCw
} from 'lucide-react';
import notificationsService from '../services/notificationsService';
import usersService from '../services/usersService';
import { useToast } from '../context/ToastContext';
import './NotificationsPage.css';

const NotificationsPage = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const toast = useToast();

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterRead, setFilterRead] = useState('all');
  const [pagination, setPagination] = useState({ current: 1, total: 1, count: 0 });
  const [unreadCount, setUnreadCount] = useState(0);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [formData, setFormData] = useState({
    sendToAll: false,
    userId: '',
    type: 'system',
    titleAr: '',
    titleEn: '',
    bodyAr: '',
    bodyEn: ''
  });

  const notificationTypes = [
    { value: 'system', label: 'نظام', labelEn: 'System' },
    { value: 'new_match', label: 'تطابق جديد', labelEn: 'New Match' },
    { value: 'offer_approved', label: 'تمت الموافقة', labelEn: 'Offer Approved' },
    { value: 'offer_rejected', label: 'تم الرفض', labelEn: 'Offer Rejected' },
    { value: 'subscription_expiring', label: 'انتهاء الاشتراك', labelEn: 'Subscription Expiring' }
  ];

  const fetchNotifications = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (filterType !== 'all') params.type = filterType;
      if (filterRead !== 'all') params.isRead = filterRead;

      const response = await notificationsService.getAll(params);
      if (response.success) {
        setNotifications(response.data.notifications || []);
        setPagination(response.data.pagination || { current: 1, total: 1, count: 0 });
        setUnreadCount(response.data.unreadCount || 0);
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [filterType, filterRead]);

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await usersService.getAll({ limit: 100 });
      if (response.success) {
        setUsers(response.data.users || []);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const filteredNotifications = notifications.filter(notification => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!notification.titleAr?.toLowerCase().includes(query) &&
          !notification.titleEn?.toLowerCase().includes(query) &&
          !notification.bodyAr?.toLowerCase().includes(query) &&
          !notification.user?.name?.toLowerCase().includes(query)) {
        return false;
      }
    }
    return true;
  });

  const handleOpenModal = () => {
    setFormData({
      sendToAll: false,
      userId: '',
      type: 'system',
      titleAr: '',
      titleEn: '',
      bodyAr: '',
      bodyEn: ''
    });
    fetchUsers();
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleSend = async () => {
    if (!formData.titleAr || !formData.titleEn) {
      toast.warning(isRTL ? 'يرجى ملء العنوان بالعربية والإنجليزية' : 'Please fill title in Arabic and English');
      return;
    }

    if (!formData.sendToAll && !formData.userId) {
      toast.warning(isRTL ? 'يرجى اختيار مستخدم أو الإرسال للجميع' : 'Please select a user or send to all');
      return;
    }

    setSending(true);
    try {
      const data = {
        type: formData.type,
        titleAr: formData.titleAr,
        titleEn: formData.titleEn,
        bodyAr: formData.bodyAr,
        bodyEn: formData.bodyEn
      };

      if (formData.sendToAll) {
        data.sendToAll = true;
      } else {
        data.userId = formData.userId;
      }

      const response = await notificationsService.sendNotification(data);
      if (response.success) {
        fetchNotifications();
        handleCloseModal();
      }
    } catch (err) {
      alert(err.message || (isRTL ? 'حدث خطأ' : 'An error occurred'));
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (notification) => {
    if (!window.confirm(isRTL ? 'هل أنت متأكد من حذف هذا الإشعار؟' : 'Are you sure you want to delete this notification?')) return;

    try {
      await notificationsService.deleteNotification(notification._id);
      fetchNotifications();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm(isRTL ? 'هل أنت متأكد من حذف جميع الإشعارات؟' : 'Are you sure you want to delete all notifications?')) return;

    try {
      await notificationsService.deleteAll();
      fetchNotifications();
    } catch (err) {
      alert(err.message);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString(isRTL ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTypeLabel = (type) => {
    const found = notificationTypes.find(t => t.value === type);
    return found ? (isRTL ? found.label : found.labelEn) : type;
  };

  return (
    <div className="notifications-page">
      <div className="page-header">
        <div className="header-title">
          <Bell size={28} />
          <div>
            <h1>{t('notifications.title', 'إدارة الإشعارات')}</h1>
            <p>{t('notifications.subtitle', 'إرسال وإدارة إشعارات المستخدمين')}</p>
          </div>
        </div>
        <div className="header-actions">
          <button className="btn btn-outline" onClick={() => fetchNotifications()}>
            <RefreshCw size={18} />
            {t('common.refresh', 'تحديث')}
          </button>
          <button className="btn btn-primary" onClick={handleOpenModal}>
            <Plus size={18} />
            {t('notifications.send', 'إرسال إشعار')}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="notifications-stats">
        <div className="stat-card">
          <span className="stat-value">{pagination.count}</span>
          <span className="stat-label">{t('notifications.total', 'إجمالي الإشعارات')}</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{unreadCount}</span>
          <span className="stat-label">{t('notifications.unread', 'غير مقروءة')}</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{pagination.count - unreadCount}</span>
          <span className="stat-label">{t('notifications.read', 'مقروءة')}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder={t('notifications.search', 'بحث في الإشعارات...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <Filter size={18} />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="all">{t('notifications.allTypes', 'جميع الأنواع')}</option>
            {notificationTypes.map(type => (
              <option key={type.value} value={type.value}>
                {isRTL ? type.label : type.labelEn}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <select
            value={filterRead}
            onChange={(e) => setFilterRead(e.target.value)}
          >
            <option value="all">{t('notifications.allStatus', 'جميع الحالات')}</option>
            <option value="false">{t('notifications.unreadOnly', 'غير مقروءة فقط')}</option>
            <option value="true">{t('notifications.readOnly', 'مقروءة فقط')}</option>
          </select>
        </div>

        {notifications.length > 0 && (
          <button className="btn btn-danger-outline" onClick={handleDeleteAll}>
            <Trash2 size={16} />
            {t('notifications.deleteAll', 'حذف الكل')}
          </button>
        )}
      </div>

      {/* Notifications List */}
      {loading ? (
        <div className="loading-container">
          <Loader className="spin" size={40} />
        </div>
      ) : filteredNotifications.length === 0 ? (
        <div className="empty-state">
          <Bell size={48} />
          <p>{t('notifications.empty', 'لا توجد إشعارات')}</p>
        </div>
      ) : (
        <div className="notifications-list">
          {filteredNotifications.map((notification) => (
            <div
              key={notification._id}
              className={`notification-card ${notification.isRead ? 'read' : 'unread'}`}
            >
              <div className="notification-icon">
                {notification.isRead ? (
                  <CheckCircle size={20} className="read-icon" />
                ) : (
                  <Clock size={20} className="unread-icon" />
                )}
              </div>
              <div className="notification-content">
                <div className="notification-header">
                  <h4>{isRTL ? notification.titleAr : notification.titleEn}</h4>
                  <span className={`type-badge ${notification.type}`}>
                    {getTypeLabel(notification.type)}
                  </span>
                </div>
                <p className="notification-body">
                  {isRTL ? notification.bodyAr : notification.bodyEn}
                </p>
                <div className="notification-meta">
                  {notification.user && (
                    <span className="user-info">
                      <User size={14} />
                      {notification.user.name || notification.user.email}
                    </span>
                  )}
                  <span className="date">{formatDate(notification.createdAt)}</span>
                </div>
              </div>
              <div className="notification-actions">
                <button
                  className="action-btn delete"
                  onClick={() => handleDelete(notification)}
                  title={t('common.delete', 'حذف')}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.total > 1 && (
        <div className="pagination">
          <button
            disabled={pagination.current === 1}
            onClick={() => fetchNotifications(pagination.current - 1)}
          >
            {t('common.previous', 'السابق')}
          </button>
          <span>
            {t('common.page', 'صفحة')} {pagination.current} {t('common.of', 'من')} {pagination.total}
          </span>
          <button
            disabled={pagination.current === pagination.total}
            onClick={() => fetchNotifications(pagination.current + 1)}
          >
            {t('common.next', 'التالي')}
          </button>
        </div>
      )}

      {/* Send Notification Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <Send size={20} />
                {t('notifications.sendNew', 'إرسال إشعار جديد')}
              </h2>
              <button className="close-btn" onClick={handleCloseModal}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              {/* Recipient */}
              <div className="form-group">
                <label>{t('notifications.recipient', 'المستلم')}</label>
                <div className="recipient-toggle">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.sendToAll}
                      onChange={(e) => setFormData({ ...formData, sendToAll: e.target.checked, userId: '' })}
                    />
                    <Users size={16} />
                    {t('notifications.sendToAll', 'إرسال لجميع المستخدمين')}
                  </label>
                </div>
                {!formData.sendToAll && (
                  <select
                    value={formData.userId}
                    onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
                    disabled={loadingUsers}
                  >
                    <option value="">{t('notifications.selectUser', 'اختر مستخدم...')}</option>
                    {users.map(user => (
                      <option key={user._id} value={user._id}>
                        {user.name} ({user.email})
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Type */}
              <div className="form-group">
                <label>{t('notifications.type', 'نوع الإشعار')}</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                >
                  {notificationTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {isRTL ? type.label : type.labelEn}
                    </option>
                  ))}
                </select>
              </div>

              {/* Title Arabic */}
              <div className="form-group">
                <label>{t('notifications.titleAr', 'العنوان (عربي)')} *</label>
                <input
                  type="text"
                  value={formData.titleAr}
                  onChange={(e) => setFormData({ ...formData, titleAr: e.target.value })}
                  placeholder="أدخل العنوان بالعربية..."
                  dir="rtl"
                />
              </div>

              {/* Title English */}
              <div className="form-group">
                <label>{t('notifications.titleEn', 'العنوان (إنجليزي)')} *</label>
                <input
                  type="text"
                  value={formData.titleEn}
                  onChange={(e) => setFormData({ ...formData, titleEn: e.target.value })}
                  placeholder="Enter title in English..."
                  dir="ltr"
                />
              </div>

              {/* Body Arabic */}
              <div className="form-group">
                <label>{t('notifications.bodyAr', 'المحتوى (عربي)')}</label>
                <textarea
                  value={formData.bodyAr}
                  onChange={(e) => setFormData({ ...formData, bodyAr: e.target.value })}
                  placeholder="أدخل المحتوى بالعربية..."
                  dir="rtl"
                  rows={3}
                />
              </div>

              {/* Body English */}
              <div className="form-group">
                <label>{t('notifications.bodyEn', 'المحتوى (إنجليزي)')}</label>
                <textarea
                  value={formData.bodyEn}
                  onChange={(e) => setFormData({ ...formData, bodyEn: e.target.value })}
                  placeholder="Enter content in English..."
                  dir="ltr"
                  rows={3}
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleCloseModal}>
                {t('common.cancel', 'إلغاء')}
              </button>
              <button className="btn btn-primary" onClick={handleSend} disabled={sending}>
                {sending ? (
                  <>
                    <Loader className="spin" size={16} />
                    {t('notifications.sending', 'جاري الإرسال...')}
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    {t('notifications.send', 'إرسال')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;
